import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { normalizeLang } from "@/lib/i18n";
import { openHelp } from "@/lib/help";

export const HELP_COPY = {
  en: { button: "Help", menu: "Help & Support" },
  "pt-BR": { button: "Ajuda", menu: "Ajuda e suporte" },
  "es-419": { button: "Ayuda", menu: "Ayuda y soporte" },
  "fr-CA": { button: "Aide", menu: "Aide et soutien" },
};

export function getHelpCopy(lang) {
  const normalized = normalizeLang(lang);
  return HELP_COPY[normalized] || HELP_COPY.en;
}

/**
 * The Help entry point: opens the help panel (HelpPanel.jsx, HELP_SYSTEM_PLAN.md
 * phase 1). `disabled` greys it out, e.g. during an Advanced Mode answer (D5).
 */
export function HelpButton({ className = "", onClick, disabled = false }) {
  const { user } = useAuth();
  const copy = getHelpCopy(user?.preferred_language);

  return (
    <button
      type="button"
      onClick={onClick || (() => openHelp())}
      disabled={disabled}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[color:var(--border-stone)] bg-white px-3 py-1.5 text-[11px] font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
      data-testid="help-button"
      aria-label={copy.menu}
      title={copy.menu}
    >
      <span
        aria-hidden="true"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[color:var(--brand)] text-[10px] font-medium leading-none text-[color:var(--brand)]"
      >
        ?
      </span>
      <span className="hidden sm:inline">{copy.button}</span>
    </button>
  );
}

function usesUserMenuHeader(pathname) {
  return (
    pathname === "/upload" ||
    /^\/prepare\/[^/]+$/.test(pathname) ||
    pathname === "/downloads" ||
    pathname === "/free-complete" ||
    pathname === "/admin" ||
    pathname.startsWith("/admin/")
  );
}

/**
 * Places the same Help control into authenticated screens that do not render
 * it directly. Interview and Report now expose a canonical persistent cluster;
 * Help always occupies the middle position between Sessions used and Account.
 */
export default function GlobalHelpAccess() {
  const { user } = useAuth();
  const location = useLocation();
  const [target, setTarget] = useState(null);
  const [floating, setFloating] = useState(false);

  useEffect(() => {
    setTarget(null);
    setFloating(false);

    if (typeof document === "undefined") return undefined;
    const pathname = location.pathname;
    // Signed out: Help floats on the sign-in page only, for people who can't get in.
    if (!user) {
      if (pathname === "/login") setFloating(true);
      return undefined;
    }
    if (usesUserMenuHeader(pathname)) return undefined;

    const isInterview = /^\/interview\/[^/]+$/.test(pathname);
    const isReport = /^\/report\/[^/]+$/.test(pathname);
    const isBetaFeedback = /^\/beta-feedback\/[^/]+$/.test(pathname);

    if (!isInterview && !isReport && !isBetaFeedback) {
      setFloating(true);
      return undefined;
    }

    let currentHost = null;
    let observer = null;
    let betaHeaderRow = null;
    let betaPriorPosition = "";

    const ensureHost = (parent, className, before = null) => {
      if (!parent) return null;
      if (!currentHost) {
        currentHost = document.createElement("div");
        currentHost.setAttribute("data-testid", "global-help-host");
      }
      currentHost.className = className;
      if (currentHost.parentElement !== parent) {
        currentHost.remove();
        parent.insertBefore(currentHost, before);
      } else if (before && before !== currentHost && currentHost.nextSibling !== before) {
        parent.insertBefore(currentHost, before);
      }
      setTarget((existing) => (existing === currentHost ? existing : currentHost));
      setFloating(false);
      return currentHost;
    };

    const attachToPersistentCluster = (scope) => {
      const persistent = scope?.querySelector('[data-testid="standard-persistent-controls"]');
      if (!persistent) return false;
      const accountHost = persistent.querySelector('[data-testid="standard-account-host"]');
      ensureHost(persistent, "shrink-0", accountHost || null);
      return true;
    };

    const sync = () => {
      if (isInterview) {
        const warmupGate = document.querySelector('[data-testid="warmup-gate"]');
        if (warmupGate) return attachToPersistentCluster(warmupGate);

        const interviewRoot = document.querySelector('[data-testid="interview-container"]');
        if (!interviewRoot) return false;
        return attachToPersistentCluster(interviewRoot);
      }

      if (isReport) {
        const reportRoot = document.querySelector('[data-testid="report-container"]');
        if (!reportRoot) return false;
        return attachToPersistentCluster(reportRoot);
      }

      if (isBetaFeedback) {
        const page = document.querySelector('[data-testid="beta-feedback-container"]');
        const headerRow = page?.querySelector("header > div");
        const backButton = headerRow?.querySelector('[data-testid="beta-feedback-back-button"]');
        if (!page || !headerRow || !backButton) return false;

        if (betaHeaderRow !== headerRow) {
          betaHeaderRow = headerRow;
          betaPriorPosition = headerRow.style.position || "";
        }
        if (!headerRow.style.position) headerRow.style.position = "relative";

        const host = ensureHost(
          headerRow,
          "absolute top-1/2 -translate-y-1/2 z-20"
        );
        if (host) {
          const backWidth = Math.ceil(backButton.getBoundingClientRect().width || 0);
          host.style.right = `${backWidth + 14}px`;
        }
        return true;
      }

      return false;
    };

    sync();
    observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });

    const onResize = () => sync();
    window.addEventListener("resize", onResize);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", onResize);
      if (currentHost?.parentElement) currentHost.remove();
      if (betaHeaderRow) betaHeaderRow.style.position = betaPriorPosition;
      setTarget(null);
    };
  }, [location.pathname, user?.user_id]);

  if (!user && !floating) return null;

  if (target) {
    return createPortal(<HelpButton className="h-10 px-4 py-0 text-sm" />, target);
  }

  if (floating) {
    return (
      <div className="fixed right-4 top-12 z-[70]" data-testid="floating-help-host">
        <HelpButton />
      </div>
    );
  }

  return null;
}
